import {
  accountTransactionsResponseSchema,
  bankAccountSchema,
  creditCardSchema,
  investmentSchema,
  plaidItemDeleteResultSchema,
  plaidItemSchema,
  plaidItemSummarySchema,
  plaidLinkTokenResponseSchema,
  plaidStatusSchema,
  plaidSyncResultSchema,
  plaidTransactionsSyncResultSchema,
  searchResponseSchema,
  summarySchema,
  type AccountTransactionsResponse,
  type BankAccount,
  type BankAccountCreate,
  type BankAccountUpdate,
  type CreditCard,
  type CreditCardCreate,
  type CreditCardUpdate,
  type Investment,
  type InvestmentCreate,
  type InvestmentUpdate,
  type PlaidExchangeRequest,
  type PlaidItem,
  type PlaidItemDeleteResult,
  type PlaidItemSummary,
  type PlaidLinkTokenResponse,
  type PlaidStatus,
  type PlaidSyncResult,
  type PlaidTransactionsSyncResult,
  type SearchResponse,
  type Source,
  type Summary,
  type SyncStatus,
  type TransactionDirection,
} from "@finance/shared";

import { API_BASE_URL, authedFetch } from "@/lib/auth";

export { API_BASE_URL };

export class ApiError extends Error {
  status: number;
  details: unknown;
  // Human-readable explanation from the response body, when the server
  // provided one. `message` carries the machine-readable `error` code so it
  // can be matched in `catch` blocks; `serverMessage` is what to show users.
  serverMessage: string | null;
  constructor(
    message: string,
    status: number,
    details?: unknown,
    serverMessage?: string | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
    this.serverMessage = serverMessage ?? null;
  }
}

async function fetchJson(path: string, signal?: AbortSignal): Promise<unknown> {
  const res = await authedFetch(path, { signal });
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status} ${res.statusText} (${path})`);
  }
  return (await res.json()) as unknown;
}

async function request(
  path: string,
  init: RequestInit & { signal?: AbortSignal } = {},
): Promise<unknown> {
  const res = await authedFetch(path, init);
  if (res.status === 204) return null;
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const error =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    const details =
      body && typeof body === "object" && "details" in body
        ? (body as { details: unknown }).details
        : undefined;
    const serverMessage =
      body &&
      typeof body === "object" &&
      "message" in body &&
      typeof (body as { message: unknown }).message === "string"
        ? ((body as { message: string }).message)
        : null;
    throw new ApiError(error, res.status, details, serverMessage);
  }
  return body;
}

export async function fetchSummary(signal?: AbortSignal): Promise<Summary> {
  const json = await fetchJson("/summary", signal);
  return summarySchema.parse(json);
}

export type RecordCategory = "bank" | "credit" | "investment";

export type RefreshResource =
  | "bank-accounts"
  | "credit-cards"
  | "investments";

const REFRESH_RESOURCE_BY_CATEGORY: Record<RecordCategory, RefreshResource> = {
  bank: "bank-accounts",
  credit: "credit-cards",
  investment: "investments",
};

export function refreshResourceForCategory(
  category: RecordCategory,
): RefreshResource {
  return REFRESH_RESOURCE_BY_CATEGORY[category];
}

export interface RecentRecord {
  id: string;
  category: RecordCategory;
  categoryLabel: string;
  name: string;
  subtitle: string;
  source: Source;
  last_updated_at: string;
  last_synced_at: string | null;
  last_sync_status: SyncStatus | null;
  amount: number;
  amountKind: "asset" | "liability";
}

const BANK_TYPE_LABEL: Record<BankAccount["type"], string> = {
  checking: "Checking",
  savings: "Savings",
  cd: "CD",
};

function toBankRecord(row: BankAccount): RecentRecord {
  return {
    id: row.id,
    category: "bank",
    categoryLabel: BANK_TYPE_LABEL[row.type],
    name: row.name,
    subtitle: row.institution,
    source: row.source,
    last_updated_at: row.last_updated_at,
    last_synced_at: row.last_synced_at ?? null,
    last_sync_status: row.last_sync_status ?? null,
    amount: row.current_balance,
    amountKind: "asset",
  };
}

function toCreditRecord(row: CreditCard): RecentRecord {
  return {
    id: row.id,
    category: "credit",
    categoryLabel: "Credit card",
    name: row.name,
    subtitle: row.issuer,
    source: row.source,
    last_updated_at: row.last_updated_at,
    last_synced_at: row.last_synced_at ?? null,
    last_sync_status: row.last_sync_status ?? null,
    amount: row.current_balance_owed,
    amountKind: "liability",
  };
}

function toInvestmentRecord(row: Investment): RecentRecord {
  return {
    id: row.id,
    category: "investment",
    categoryLabel: "Investment",
    name: row.name,
    subtitle: row.ticker ?? "—",
    source: row.source,
    last_updated_at: row.last_updated_at,
    last_synced_at: row.last_synced_at ?? null,
    last_sync_status: row.last_sync_status ?? null,
    amount: row.current_value,
    amountKind: "asset",
  };
}

async function fetchList<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  const json = await fetchJson(path, signal);
  if (!Array.isArray(json)) {
    throw new Error(`Expected array from ${path}`);
  }
  return json as T[];
}

export async function fetchRecentRecords(
  limit: number,
  signal?: AbortSignal,
): Promise<RecentRecord[]> {
  const [banks, credit, investments] = await Promise.all([
    fetchList<BankAccount>("/bank-accounts", signal),
    fetchList<CreditCard>("/credit-cards", signal),
    fetchList<Investment>("/investments", signal),
  ]);

  const combined: RecentRecord[] = [
    ...banks.map(toBankRecord),
    ...credit.map(toCreditRecord),
    ...investments.map(toInvestmentRecord),
  ];

  combined.sort((a, b) =>
    a.last_updated_at < b.last_updated_at ? 1 : a.last_updated_at > b.last_updated_at ? -1 : 0,
  );

  return combined.slice(0, limit);
}

export async function listBankAccounts(signal?: AbortSignal): Promise<BankAccount[]> {
  return fetchList<BankAccount>("/bank-accounts", signal);
}

export async function createBankAccount(input: BankAccountCreate): Promise<BankAccount> {
  const json = await request("/bank-accounts", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return bankAccountSchema.parse(json);
}

export async function updateBankAccount(
  id: string,
  input: BankAccountUpdate,
): Promise<BankAccount> {
  const json = await request(`/bank-accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return bankAccountSchema.parse(json);
}

export async function deleteBankAccount(id: string): Promise<void> {
  await request(`/bank-accounts/${id}`, { method: "DELETE" });
}

export async function listCreditCards(signal?: AbortSignal): Promise<CreditCard[]> {
  return fetchList<CreditCard>("/credit-cards", signal);
}

export async function createCreditCard(input: CreditCardCreate): Promise<CreditCard> {
  const json = await request("/credit-cards", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return creditCardSchema.parse(json);
}

export async function updateCreditCard(
  id: string,
  input: CreditCardUpdate,
): Promise<CreditCard> {
  const json = await request(`/credit-cards/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return creditCardSchema.parse(json);
}

export async function deleteCreditCard(id: string): Promise<void> {
  await request(`/credit-cards/${id}`, { method: "DELETE" });
}

export async function listInvestments(signal?: AbortSignal): Promise<Investment[]> {
  return fetchList<Investment>("/investments", signal);
}

export async function createInvestment(input: InvestmentCreate): Promise<Investment> {
  const json = await request("/investments", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return investmentSchema.parse(json);
}

export async function updateInvestment(
  id: string,
  input: InvestmentUpdate,
): Promise<Investment> {
  const json = await request(`/investments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return investmentSchema.parse(json);
}

export async function deleteInvestment(id: string): Promise<void> {
  await request(`/investments/${id}`, { method: "DELETE" });
}

const REFRESH_PARSER = {
  "bank-accounts": bankAccountSchema,
  "credit-cards": creditCardSchema,
  investments: investmentSchema,
} as const;

export async function refreshRecord(
  resource: RefreshResource,
  id: string,
): Promise<void> {
  const json = await request(`/${resource}/${id}/refresh`, { method: "POST" });
  REFRESH_PARSER[resource].parse(json);
}

export async function fetchPlaidStatus(signal?: AbortSignal): Promise<PlaidStatus> {
  const json = await fetchJson("/plaid/status", signal);
  return plaidStatusSchema.parse(json);
}

export async function listPlaidItems(signal?: AbortSignal): Promise<PlaidItem[]> {
  const json = await fetchJson("/plaid/items", signal);
  if (!Array.isArray(json)) throw new Error("Expected array from /plaid/items");
  return json.map((row) => plaidItemSchema.parse(row));
}

export async function createPlaidLinkToken(): Promise<PlaidLinkTokenResponse> {
  const json = await request("/plaid/link-token", { method: "POST" });
  return plaidLinkTokenResponseSchema.parse(json);
}

export async function exchangePlaidPublicToken(
  input: PlaidExchangeRequest,
): Promise<PlaidItem> {
  const json = await request("/plaid/exchange", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return plaidItemSchema.parse(json);
}

export async function syncPlaidItem(itemId: string): Promise<PlaidSyncResult> {
  const json = await request(`/plaid/sync/${encodeURIComponent(itemId)}`, {
    method: "POST",
  });
  return plaidSyncResultSchema.parse(json);
}

export async function getPlaidItemSummary(
  itemId: string,
  signal?: AbortSignal,
): Promise<PlaidItemSummary> {
  const json = await fetchJson(
    `/plaid/items/${encodeURIComponent(itemId)}/summary`,
    signal,
  );
  return plaidItemSummarySchema.parse(json);
}

export async function deletePlaidItem(
  itemId: string,
): Promise<PlaidItemDeleteResult> {
  const json = await request(`/plaid/items/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
  });
  return plaidItemDeleteResultSchema.parse(json);
}

export async function syncPlaidTransactions(
  itemId: string,
): Promise<PlaidTransactionsSyncResult> {
  const json = await request(
    `/plaid/transactions/sync/${encodeURIComponent(itemId)}`,
    { method: "POST" },
  );
  return plaidTransactionsSyncResultSchema.parse(json);
}

export async function globalSearch(
  query: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const json = await fetchJson(
    `/search?q=${encodeURIComponent(query)}`,
    signal,
  );
  return searchResponseSchema.parse(json);
}

export async function getAccountTransactions(
  accountId: string,
  opts: {
    limit?: number;
    offset?: number;
    direction?: TransactionDirection | null;
    search?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<AccountTransactionsResponse> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  if (opts.direction) params.set("direction", opts.direction);
  if (opts.search) {
    const trimmed = opts.search.trim();
    if (trimmed) params.set("q", trimmed);
  }
  const query = params.toString();
  const json = await fetchJson(
    `/accounts/${encodeURIComponent(accountId)}/transactions${query ? `?${query}` : ""}`,
    opts.signal,
  );
  return accountTransactionsResponseSchema.parse(json);
}
